import { useMemo } from 'react'

import { Avatar } from 'antd'
import styled, { css } from 'styled-components'

import { Count, useInboxProvider } from '../layout-components/CorePageInbox'

import { deterministicThemeColour, getUserInitials } from '@/utils/index'

type UserAvatarProps = {
  user: {
    firstName?: string
    lastName?: string
    email?: string
    profilePicture?: {
      url?: string
    }
  }
  isInSupportMode?: boolean
  isButton?: boolean
  size?: number
  style?: React.CSSProperties
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const commonStyles = css<{
  $isButton?: boolean
  $isInSupportMode?: boolean
  $size?: number
}>`
  width: ${({ $size }) => $size || 40}px;
  height: ${({ $size }) => $size || 40}px;
  border: none;
  padding: 0;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colour.neutral.white};
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.022em;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;

  ${({ $isInSupportMode }) =>
    $isInSupportMode &&
    css`
      border: 2px solid ${({ theme }) => theme.colour.primary.base};
    `}

  ${({ $isButton }) =>
    $isButton &&
    css`
      cursor: pointer;

      &:hover {
        box-shadow: rgba(0, 0, 0, 0.15) 0px 0px 0px 20rem inset;
      }
    `}
`

const UserInitialsSquare = styled.button<{
  $isButton?: boolean
  $isInSupportMode?: boolean
  $size?: number
}>`
  ${commonStyles}
`

const StyledAvatar = styled(Avatar)<{
  $isButton?: boolean
  $isInSupportMode?: boolean
  $size?: number
}>`
  ${commonStyles}
`

const User = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const UserName = styled.span`
  ${({ theme }) => theme.font.body03};
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary._700};
`

const UserEmail = styled.span`
  font-size: 0.625rem;
  line-height: 150%;
  color: ${({ theme }) => theme.colour.secondary._400};
  letter-spacing: 0.3px;
`

const FloatingCount = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  z-index: 1;

  ${({ theme: { media } }) => media.lg`
    top: 0.5rem;
    right: 0.5rem;
  `}
`

export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  isInSupportMode,
  isButton,
  size = 40,
  style,
  ...rest
}) => {
  const initials = useMemo(() => getUserInitials(user), [user])
  const background = useMemo(
    () => deterministicThemeColour(initials),
    [initials]
  )
  const { unreadCount } = useInboxProvider()

  return (
    <Wrapper>
      {user.profilePicture?.url ? (
        <StyledAvatar
          src={user.profilePicture?.url}
          style={{ width: size, height: size, ...style }}
          $isButton={isButton}
          $isInSupportMode={isInSupportMode}
          $size={size}
          {...rest}
        />
      ) : (
        <UserInitialsSquare
          style={{ background, width: size, height: size, ...style }}
          as={isButton ? 'button' : 'div'}
          $isInSupportMode={isInSupportMode}
          $isButton={isButton}
          $size={size}
          {...rest}
        >
          {initials}
        </UserInitialsSquare>
      )}
      {!isButton && (
        <User>
          <UserName>{user.firstName}</UserName>
          <UserEmail>{user.email}</UserEmail>
        </User>
      )}
      {!!unreadCount && (
        <FloatingCount>
          <Count>{unreadCount}</Count>
        </FloatingCount>
      )}
    </Wrapper>
  )
}
